import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, BedDouble, DoorOpen, MoreHorizontal, Plus, Ruler } from "lucide-react";
import client, { unwrap } from "@/api/client";
import { cn } from "@/lib/utils";
import { roomImageFor } from "@/lib/stockImages";
import { Field, IconInput } from "@/components/forms/FormField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

export const formatRupees = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const ROOM_SUGGESTIONS = ["Living Room", "Master Bedroom", "Kids Bedroom", "Guest Bedroom", "Dining Room", "Kitchen", "Study", "Balcony"];

function RoomCard({ room, windowCount, productCount, subtotal, isActive, onSelect, onAddWindow }) {
  return (
    <div
      data-testid={`room-card-${room._id}`}
      className={cn(
        "group relative h-56 rounded-2xl overflow-hidden border transition-all",
        isActive ? "border-accent ring-2 ring-accent/40" : "border-border hover:shadow-md",
      )}
    >
      <button onClick={onSelect} aria-pressed={isActive} aria-label={`${room.name}: show windows`} className="absolute inset-0 text-left">
        <img
          src={roomImageFor(room.name)}
          alt=""
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
        <span className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
        <span className="absolute inset-x-0 bottom-0 flex items-end gap-3 p-4">
          <BedDouble className="h-5 w-5 text-white/90 mb-1 shrink-0" strokeWidth={1.6} />
          <span className="min-w-0">
            <span className="block font-serif text-xl text-white truncate">{room.name}</span>
            <span className="block text-xs text-white/85 mt-0.5">
              {plural(windowCount, "Window")} · {plural(productCount, "Product")} · {formatRupees(subtotal)}
            </span>
          </span>
        </span>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            aria-label={`Actions for ${room.name}`}
            data-testid={`room-menu-${room._id}`}
            className="absolute top-3 right-3 h-8 w-8 rounded-full bg-card/90 backdrop-blur text-foreground flex items-center justify-center shadow-sm hover:bg-card"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={onSelect}>{isActive ? "Hide windows" : "Show windows"}</DropdownMenuItem>
          <DropdownMenuItem onSelect={onAddWindow}>Add window</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function AddRoomTile({ onClick, first }) {
  return (
    <button
      data-testid="add-room-tile"
      onClick={onClick}
      className="h-56 rounded-2xl border border-dashed border-border bg-secondary/30 hover:bg-secondary/60 hover:border-foreground/25 transition-colors flex flex-col items-center justify-center text-center px-6"
    >
      <span className="h-11 w-11 rounded-full bg-card border border-border flex items-center justify-center shadow-sm">
        <Plus className="h-5 w-5 text-foreground/80" />
      </span>
      <span className="mt-3 text-[15px] font-medium text-foreground">{first ? "Add your first room" : "Add another room"}</span>
      <span className="mt-1 text-sm text-muted-foreground max-w-[15rem]">Break your project into rooms for better planning.</span>
    </button>
  );
}

function AddRoomDialog({ open, setOpen, projectId }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const createRoom = useMutation({
    mutationFn: (payload) => client.post("/rooms", payload),
    onSuccess: () => {
      toast.success("Room added");
      setOpen(false);
      setName("");
      queryClient.invalidateQueries({ queryKey: ["rooms", projectId] });
    },
    onError: (err) => toast.error(err?.response?.data?.error?.message || "Failed to add room"),
  });
  const submit = (e) => {
    e.preventDefault();
    if (name.trim()) createRoom.mutate({ projectId, name: name.trim() });
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="add-room-dialog" className="sm:max-w-md rounded-2xl">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-normal">Add Room</DialogTitle>
            <DialogDescription>Each room holds its own windows and product designs.</DialogDescription>
          </DialogHeader>
          <Field label="Room Name" required htmlFor="room-name" className="mt-5">
            <IconInput id="room-name" icon={DoorOpen} data-testid="room-name-input" placeholder="e.g. Living Room" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {ROOM_SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setName(s)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs transition-colors",
                  name === s ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                {s}
              </button>
            ))}
          </div>
          <Button
            type="submit"
            data-testid="room-submit-button"
            disabled={!name.trim() || createRoom.isPending}
            className="mt-6 w-full bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            Create Room
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_WINDOW = { width: "", height: "", unit: "inch", windowType: "", quantity: 1, notes: "" };

function WindowsPanel({ room, projectId, addOpen, setAddOpen }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY_WINDOW);

  const { data: windows = [] } = useQuery({
    queryKey: ["windows", room._id],
    queryFn: () => client.get(`/windows?roomId=${room._id}`).then(unwrap),
  });

  const createWindow = useMutation({
    mutationFn: (payload) => client.post("/windows", { ...payload, roomId: room._id }),
    onSuccess: () => {
      toast.success("Window added");
      setAddOpen(false);
      setForm(EMPTY_WINDOW);
      queryClient.invalidateQueries({ queryKey: ["windows", room._id] });
      queryClient.invalidateQueries({ queryKey: ["room-window-counts", projectId] });
    },
    onError: (err) => toast.error(err?.response?.data?.error?.message || "Failed to add window"),
  });

  return (
    <div className="rounded-2xl border border-border bg-secondary/30 p-4 sm:p-5 hi-animate-fade-up" data-testid={`windows-panel-${room._id}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <p className="font-serif text-lg text-foreground">{room.name} — Windows</p>
          <p className="text-xs text-muted-foreground">Open a window to measure, design and preview it.</p>
        </div>
        <Button size="sm" variant="outline" className="bg-card shrink-0" data-testid={`add-window-button-${room._id}`} onClick={() => setAddOpen(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Window
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {windows.map((w) => (
          <Link
            key={w._id}
            to={`/windows/${w._id}?projectId=${projectId}&roomId=${room._id}`}
            data-testid={`window-card-${w._id}`}
            className="group flex items-center gap-3 rounded-xl border border-border bg-card p-3 hover:border-accent/50 transition-colors"
          >
            <span className="h-10 w-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center shrink-0">
              <Ruler className="h-4 w-4" strokeWidth={1.75} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium text-foreground truncate">{w.windowType || "Window"}</span>
              <span className="block text-xs text-muted-foreground">
                {w.width} × {w.height} {w.unit} · Qty {w.quantity}
              </span>
            </span>
            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
          </Link>
        ))}
        {windows.length === 0 && (
          <p className="text-sm text-muted-foreground col-span-full py-3">No windows yet — add one to start designing this room.</p>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid={`add-window-dialog-${room._id}`} className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-normal">Add Window</DialogTitle>
            <DialogDescription>{room.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Width</Label><Input data-testid="window-width-input" type="number" value={form.width} onChange={(e) => setForm({ ...form, width: e.target.value })} /></div>
              <div><Label>Height</Label><Input data-testid="window-height-input" type="number" value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} /></div>
              <div><Label>Unit</Label><Input data-testid="window-unit-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Window Type</Label><Input data-testid="window-type-input" placeholder="e.g. Sliding" value={form.windowType} onChange={(e) => setForm({ ...form, windowType: e.target.value })} /></div>
              <div><Label>Quantity</Label><Input data-testid="window-quantity-input" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea data-testid="window-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <Button
              data-testid="window-submit-button"
              className="w-full bg-accent hover:bg-accent/90 text-accent-foreground"
              disabled={!form.width || !form.height || createWindow.isPending}
              onClick={() => createWindow.mutate({ ...form, width: Number(form.width), height: Number(form.height), quantity: Number(form.quantity) })}
            >
              Add Window
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function RoomsSection({ projectId, rooms, cartItems }) {
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [addRoomOpen, setAddRoomOpen] = useState(false);
  const [addWindowOpen, setAddWindowOpen] = useState(false);

  const { data: roomWindowCounts = {} } = useQuery({
    queryKey: ["room-window-counts", projectId, rooms.map((r) => r._id).join(",")],
    queryFn: async () => {
      const counts = {};
      await Promise.all(
        rooms.map(async (r) => {
          const ws = await client.get(`/windows?roomId=${r._id}`).then(unwrap).catch(() => []);
          counts[r._id] = ws.length;
        }),
      );
      return counts;
    },
    enabled: rooms.length > 0,
  });

  const itemsFor = (roomId) => cartItems.filter((it) => it.roomId === roomId);
  const activeRoom = rooms.find((r) => r._id === activeRoomId);

  return (
    <section className="bg-card border border-border rounded-2xl p-5 sm:p-6" data-testid="project-rooms">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="font-serif text-xl text-foreground">
            Rooms <span className="font-sans text-sm text-muted-foreground">({rooms.length})</span>
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">Manage and view rooms for this project.</p>
        </div>
        <Button data-testid="add-room-button" onClick={() => setAddRoomOpen(true)} className="bg-accent hover:bg-accent/90 text-accent-foreground shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> Add Room
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {rooms.map((r) => {
          const items = itemsFor(r._id);
          return (
            <RoomCard
              key={r._id}
              room={r}
              windowCount={roomWindowCounts[r._id] ?? 0}
              productCount={items.length}
              subtotal={items.reduce((s, it) => s + (it.price || 0), 0)}
              isActive={activeRoomId === r._id}
              onSelect={() => setActiveRoomId(activeRoomId === r._id ? null : r._id)}
              onAddWindow={() => {
                setActiveRoomId(r._id);
                setAddWindowOpen(true);
              }}
            />
          );
        })}
        <AddRoomTile first={rooms.length === 0} onClick={() => setAddRoomOpen(true)} />
      </div>

      {activeRoom && (
        <div className="mt-5">
          <WindowsPanel key={activeRoom._id} room={activeRoom} projectId={projectId} addOpen={addWindowOpen} setAddOpen={setAddWindowOpen} />
        </div>
      )}

      <AddRoomDialog open={addRoomOpen} setOpen={setAddRoomOpen} projectId={projectId} />
    </section>
  );
}
